import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { authenticationService } from "../api/authentication.service";
import { formatDate } from "../api/formatDate";
import { plansService } from "../api/plans.service";
import { marketingEventsService } from "../api/marketingEvents.service";
import { userService } from "../api/user.service";
import "../assets/styles/pages/lists-transactions.scss";
import InformationCreditsModal from "../components/InformationCreditsModal";
import MainStructure from "../components/containers/MainStructure";
import Pagination from "../components/elements/Pagination";
import { UserContext } from "../contexts/UserContext";

const PAGE_SIZE = 10;

export function getTransactionPresentation(transaction) {
	const status = String(transaction.status || "").toLowerCase();
	const type = String(transaction.transaction_type || "").toLowerCase();
	const description = String(transaction.description || "").toLowerCase();

	if (status === "refunded") return { label: "Refunded", className: "is-refunded", icon: "fa-undo" };
	if (status === "pending") return { label: "Pending", className: "is-pending", icon: "fa-clock" };
	if (status === "failed") return { label: "Failed", className: "is-failed", icon: "fa-exclamation-circle" };
	if (type === "expired" || description.includes("expired")) return { label: "Expired", className: "is-expired", icon: "fa-calendar-times" };
	if (["redemed", "redeemed"].includes(type) || Number(transaction.amount) < 0) {
		return { label: "Used for report", className: "is-used", icon: "fa-file-alt" };
	}
	return { label: "Purchased", className: "is-purchased", icon: "fa-plus-circle" };
}

export function formatTransactionDescription(transaction, presentation) {
	const cleaned = String(transaction.description || "")
		.replace(/^(purchase|subscribe|deposit|redemed|redeemed)\s*/i, "")
		.trim();
	if (cleaned) return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
	if (presentation.label === "Used for report") return "Credits used to generate a report";
	if (presentation.label === "Purchased") return transaction.plan?.name || "Credits added to your account";
	return `${presentation.label} credit transaction`;
}

function Transactions() {
	const location = useLocation();
	const user = useContext(UserContext);
	const [isOpenModal, setIsOpenModal] = useState(false);
	const [data, setData] = useState({ results: [], count: 0 });
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [retryCount, setRetryCount] = useState(0);
	const [checkoutPaid, setCheckoutPaid] = useState(false);
	const sessionId = useMemo(
		() => new URLSearchParams(location.search).get("session_id"),
		[location.search]
	);

	const loadTransactions = useCallback(() => {
		let refreshUser = Promise.resolve();
		if (sessionId) {
			refreshUser = userService.details().then((updatedUser) => {
				authenticationService.updateUserDetails(updatedUser);
			});
		}

		setLoading(true);
		setLoadError(false);
		return refreshUser
			.catch(() => undefined)
			.then(() => plansService.listTransactions(`?limit=${PAGE_SIZE}&offset=${(currentPage - 1) * PAGE_SIZE}`))
			.then((response) => setData({
				results: response?.results || [],
				count: response?.count || 0,
			}))
			.catch(() => setLoadError(true))
			.finally(() => setLoading(false));
	}, [currentPage, sessionId]);

	useEffect(() => {
		loadTransactions();
	}, [loadTransactions, retryCount]);

	useEffect(() => {
		if (!sessionId) return undefined;
		let canceled = false;
		let timer;
		let attempts = 0;
		const verifyCheckout = async () => {
			try {
				const result = await marketingEventsService.recordCheckout(sessionId);
				if (canceled) return;
				if (result.paid) {
					setCheckoutPaid(true);
					window.dispatchEvent(new Event('marketing:events-ready'));
					loadTransactions();
					return;
				}
			} catch (error) {
				console.error('Unable to verify Checkout session', error);
				return;
			}
			if (++attempts < 12) timer = window.setTimeout(verifyCheckout, 5000);
		};
		verifyCheckout();
		return () => {
			canceled = true;
			window.clearTimeout(timer);
		};
	}, [sessionId, loadTransactions]);

	const totalPages = Math.ceil(data.count / PAGE_SIZE);
	const startIndex = (currentPage - 1) * PAGE_SIZE + 1;
	const endIndex = Math.min(startIndex + data.results.length - 1, data.count);
	const pageNet = data.results.reduce((total, transaction) => total + (Number(transaction.amount) || 0), 0);

	return (
		<MainStructure>
			<main className="records-page transactions-page">
				{checkoutPaid ? <p role="status">Your purchase is complete. Your credits will appear here shortly.</p> : null}
				<header className="records-page__header">
					<div>
						<p className="records-page__eyebrow">Account activity</p>
						<h1>Transactions</h1>
						<p>See when credits were added, used, refunded, or expired.</p>
					</div>
					<div className="records-header-actions">
						<button type="button" className="records-help-action" onClick={() => setIsOpenModal(true)}>
							<i className="fa fa-question-circle" aria-hidden="true" /> How credits work
						</button>
						<Link to="/membership" className="records-primary-action"><i className="fa fa-coins" aria-hidden="true" /> Buy credits</Link>
					</div>
				</header>

				<div className="records-summary records-summary--three" aria-label="Transaction summary">
					<div><i className="fa fa-wallet" aria-hidden="true" /><span><strong>{user?.credits ?? "—"}</strong> credits available</span></div>
					<div><i className="fa fa-receipt" aria-hidden="true" /><span><strong>{data.count}</strong> transactions</span></div>
					<div><i className={`fa ${pageNet < 0 ? "fa-arrow-down" : "fa-arrow-up"}`} aria-hidden="true" /><span><strong>{pageNet > 0 ? "+" : ""}{pageNet}</strong> net on this page</span></div>
				</div>

				<section className="records-panel" aria-labelledby="transaction-history-title">
					<div className="records-toolbar">
						<div>
							<h2 id="transaction-history-title">Credit history</h2>
							<p>Newest activity appears first.</p>
						</div>
					</div>

					{loading ? (
						<div className="records-state" role="status"><span className="records-spinner" /> Loading transactions…</div>
					) : loadError ? (
						<div className="records-state is-error">
							<i className="fa fa-exclamation-circle" aria-hidden="true" />
							<h3>Transactions could not be loaded</h3>
							<p>Check your connection and try again.</p>
							<button type="button" onClick={() => setRetryCount((count) => count + 1)}>Try again</button>
						</div>
					) : data.results.length ? (
						<>
							<div className="records-table-meta"><span>{startIndex}–{endIndex} of {data.count}</span></div>
							<div className="records-table-wrap">
								<table className="records-table transactions-history-table">
									<thead><tr><th>Activity</th><th>Description</th><th>Date</th><th>Credits</th></tr></thead>
									<tbody>
										{data.results.map((transaction) => {
											const presentation = getTransactionPresentation(transaction);
											const amount = Number(transaction.amount) || 0;
											return (
												<tr key={transaction.id}>
													<td data-label="Activity"><span className={`transaction-kind ${presentation.className}`}><i className={`fa ${presentation.icon}`} aria-hidden="true" /> {presentation.label}</span></td>
													<td data-label="Description"><strong>{formatTransactionDescription(transaction, presentation)}</strong><span className="record-note">Transaction #{transaction.id}</span></td>
													<td data-label="Date">{formatDate(transaction.created)}</td>
													<td data-label="Credits"><strong className={`transaction-amount ${amount < 0 ? "is-negative" : "is-positive"}`}>{amount > 0 ? "+" : ""}{amount}</strong></td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
							{totalPages > 1 ? <div className="records-pagination"><Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} /></div> : null}
						</>
					) : (
						<div className="records-state">
							<i className="fa fa-receipt" aria-hidden="true" />
							<h3>No transactions yet</h3>
							<p>Credit purchases and report usage will appear here.</p>
							<Link to="/membership">Buy credits</Link>
						</div>
					)}
				</section>
			</main>

			<InformationCreditsModal isOpenModal={isOpenModal} setIsOpenModal={setIsOpenModal} />
		</MainStructure>
	);
}

export default Transactions;
